/**
 * Notification Panel Constants
 * Shared color scheme, status configuration, and utility functions.
 */
import {
    Info,
    CheckCircle,
    Warning,
    Error as ErrorIcon
} from "@mui/icons-material";

// App-matching color scheme (dark header like the rest of the app)
export const PRIME_COLORS = {
    primary: "#155EEF",
    primaryDark: "#0040C1",
    headerBg: "#000000",           // Pure black header
    headerText: "#ffffff",
    headerHover: "rgba(255,255,255,0.1)",    // Header hover state
    headerDisabled: "rgba(255,255,255,0.3)", // Header disabled state
    surfaceBg: "#F8FAFC",          // Light surface matching app background
    cardBg: "#ffffff",
    textPrimary: "#101828",        // Matching app text colors
    textSecondary: "#667085",
    border: "#EAECF0",
    hoverBg: "#F9FAFB",
    actionHover: "rgba(0, 0, 0, 0.04)",      // Action button hover
    selectedBg: "#EEF4FF",
    selectedBorder: "#155EEF",
    unreadBadge: "#155EEF",
    flagColor: "#DC2626",
    pinColor: "#155EEF"
};

export const STATUS_CONFIG = {
    informational: { icon: Info, color: "#155EEF", bgColor: "#EEF4FF", label: "Informational" },
    success: { icon: CheckCircle, color: "#027A48", bgColor: "#ECFDF3", label: "Success" },
    warning: { icon: Warning, color: "#B54708", bgColor: "#FFFAEB", label: "Warning" },
    error: { icon: ErrorIcon, color: "#B42318", bgColor: "#FEF3F2", label: "Error" }
};

export const GROUP_ORDER = ["Pinned", "Today", "Yesterday", "Older"];

/**
 * Format timestamp for display
 */
export const formatTimestamp = (timestamp, pinned = false) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (pinned || (!isToday && !isYesterday)) {
        return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }

    return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
};

/**
 * Get date group for notification
 */
export const getDateGroup = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) {
        return "Today";
    }
    if (isYesterday) {
        return "Yesterday";
    }
    return "Older";
};

/**
 * Get chip styles based on active state (matches app button styles)
 */
export const getChipSx = (isActive) => ({
    borderRadius: "8px",
    fontWeight: 600,
    fontSize: "0.8rem",
    height: 28,
    bgcolor: isActive ? PRIME_COLORS.primary : "transparent",
    color: isActive ? "#fff" : PRIME_COLORS.headerText,
    borderColor: isActive ? PRIME_COLORS.primary : "rgba(255,255,255,0.3)",
    "&:hover": {
        bgcolor: isActive ? PRIME_COLORS.primaryDark : "rgba(255,255,255,0.1)"
    }
});

/**
 * Default filter state
 */
export const getDefaultFilters = () => ({
    sortOrder: "newest",
    unread: false,
    flagged: false,
    selectedProducts: [],
    selectedStatuses: []
});
